import type { Pt } from "./pathcmds";

/**
 * Photoshop-style mesh warp: a cols x rows grid of bicubic tensor-product Bézier patches
 * sharing a (3*cols+1) x (3*rows+1) control-point lattice, laid over a rest rectangle.
 *
 * Lattice point (i, j): i runs along x/u, j along y/v. Points with i%3==0 && j%3==0 are
 * anchors; points on patch edges are handles; the 4 points inside each patch are interior.
 */
export interface MeshWarp {
  cols: number;
  rows: number;
  bounds: { x: number; y: number; w: number; h: number };
  pts: Float64Array;
  /** When true, interior points are derived from the patch boundary (Coons-equivalent). */
  autoInterior: boolean;
}

export const latticeW = (m: MeshWarp) => m.cols * 3 + 1;
export const latticeH = (m: MeshWarp) => m.rows * 3 + 1;

export function createMesh(bounds: MeshWarp["bounds"], cols = 1, rows = 1): MeshWarp {
  const m: MeshWarp = { cols, rows, bounds, pts: new Float64Array(0), autoInterior: true };
  m.pts = restLattice(m);
  return m;
}

function restLattice(m: MeshWarp): Float64Array {
  const nu = latticeW(m), nv = latticeH(m);
  const pts = new Float64Array(nu * nv * 2);
  for (let j = 0; j < nv; j++)
    for (let i = 0; i < nu; i++) {
      pts[(j * nu + i) * 2] = m.bounds.x + (i / (nu - 1)) * m.bounds.w;
      pts[(j * nu + i) * 2 + 1] = m.bounds.y + (j / (nv - 1)) * m.bounds.h;
    }
  return pts;
}

export function getPt(m: MeshWarp, i: number, j: number): Pt {
  const k = (j * latticeW(m) + i) * 2;
  return { x: m.pts[k], y: m.pts[k + 1] };
}

export function setPt(m: MeshWarp, i: number, j: number, p: Pt) {
  const k = (j * latticeW(m) + i) * 2;
  m.pts[k] = p.x;
  m.pts[k + 1] = p.y;
}

export type PointKind = "anchor" | "handle" | "interior";
export function pointKind(i: number, j: number): PointKind {
  const a = i % 3 === 0, b = j % 3 === 0;
  return a && b ? "anchor" : a || b ? "handle" : "interior";
}

/**
 * Interior control points of the tensor patch equivalent to the bicubically blended Coons
 * patch with the same boundary (Farin, "Curves and Surfaces for CAGD", §22). Keeps our mesh
 * identical to Skia's drawPatch when no interior point has been moved by hand.
 */
export function recomputeInterior(m: MeshWarp) {
  for (let pv = 0; pv < m.rows; pv++)
    for (let pu = 0; pu < m.cols; pu++) {
      const P = (a: number, b: number) => getPt(m, pu * 3 + a, pv * 3 + b);
      const comb = (terms: [number, Pt][]): Pt => {
        let x = 0, y = 0;
        for (const [k, p] of terms) {
          x += k * p.x;
          y += k * p.y;
        }
        return { x: x / 9, y: y / 9 };
      };
      const [P00, P01, P02, P03] = [P(0, 0), P(0, 1), P(0, 2), P(0, 3)];
      const [P10, P13, P20, P23] = [P(1, 0), P(1, 3), P(2, 0), P(2, 3)];
      const [P30, P31, P32, P33] = [P(3, 0), P(3, 1), P(3, 2), P(3, 3)];
      setPt(m, pu * 3 + 1, pv * 3 + 1, comb([[-4, P00], [6, P01], [6, P10], [-2, P03], [-2, P30], [3, P31], [3, P13], [-1, P33]]));
      setPt(m, pu * 3 + 1, pv * 3 + 2, comb([[-4, P03], [6, P02], [6, P13], [-2, P00], [-2, P33], [3, P32], [3, P10], [-1, P30]]));
      setPt(m, pu * 3 + 2, pv * 3 + 1, comb([[-4, P30], [6, P31], [6, P20], [-2, P33], [-2, P00], [3, P01], [3, P23], [-1, P03]]));
      setPt(m, pu * 3 + 2, pv * 3 + 2, comb([[-4, P33], [6, P32], [6, P23], [-2, P30], [-2, P03], [3, P02], [3, P20], [-1, P00]]));
    }
}

/** Moving an anchor drags its neighbouring handles (and interior) along, like Photoshop. */
export function moveLatticePoint(m: MeshWarp, i: number, j: number, dx: number, dy: number) {
  const nu = latticeW(m), nv = latticeH(m);
  const kind = pointKind(i, j);
  const move = (a: number, b: number) => {
    if (a < 0 || b < 0 || a >= nu || b >= nv) return;
    const p = getPt(m, a, b);
    setPt(m, a, b, { x: p.x + dx, y: p.y + dy });
  };
  if (kind === "anchor") {
    for (let b = -1; b <= 1; b++) for (let a = -1; a <= 1; a++) move(i + a, j + b);
  } else {
    move(i, j);
  }
  if (m.autoInterior) recomputeInterior(m);
}

function bern(t: number, out: number[], dout: number[]) {
  const mt = 1 - t;
  out[0] = mt * mt * mt;
  out[1] = 3 * mt * mt * t;
  out[2] = 3 * mt * t * t;
  out[3] = t * t * t;
  dout[0] = -3 * mt * mt;
  dout[1] = 3 * mt * mt - 6 * mt * t;
  dout[2] = 6 * mt * t - 3 * t * t;
  dout[3] = 3 * t * t;
}

export interface MeshSample {
  x: number;
  y: number;
  /** Partial derivatives with respect to global u and v (0..1 over the whole mesh). */
  dxu: number;
  dyu: number;
  dxv: number;
  dyv: number;
}

const bu = [0, 0, 0, 0], dbu = [0, 0, 0, 0], bv = [0, 0, 0, 0], dbv = [0, 0, 0, 0];

/** Evaluate at global (u, v); outside [0,1] the surface is extended linearly from the edge. */
export function evalMesh(m: MeshWarp, u: number, v: number): MeshSample {
  const uc = Math.min(1, Math.max(0, u));
  const vc = Math.min(1, Math.max(0, v));
  const pu = Math.min(m.cols - 1, Math.floor(uc * m.cols));
  const pv = Math.min(m.rows - 1, Math.floor(vc * m.rows));
  const s = uc * m.cols - pu;
  const t = vc * m.rows - pv;
  bern(s, bu, dbu);
  bern(t, bv, dbv);
  const nu = latticeW(m);
  let x = 0, y = 0, xs = 0, ys = 0, xt = 0, yt = 0;
  for (let b = 0; b < 4; b++) {
    const row = (pv * 3 + b) * nu + pu * 3;
    for (let a = 0; a < 4; a++) {
      const k = (row + a) * 2;
      const px = m.pts[k], py = m.pts[k + 1];
      const w = bu[a] * bv[b], ws = dbu[a] * bv[b], wt = bu[a] * dbv[b];
      x += w * px;
      y += w * py;
      xs += ws * px;
      ys += ws * py;
      xt += wt * px;
      yt += wt * py;
    }
  }
  const r: MeshSample = { x, y, dxu: xs * m.cols, dyu: ys * m.cols, dxv: xt * m.rows, dyv: yt * m.rows };
  if (u !== uc || v !== vc) {
    r.x += r.dxu * (u - uc) + r.dxv * (v - vc);
    r.y += r.dyu * (u - uc) + r.dyv * (v - vc);
  }
  return r;
}

/** Warp a point given in rest (pre-warp) coordinates. */
export function warpPoint(m: MeshWarp, x: number, y: number): MeshSample {
  const s = evalMesh(m, (x - m.bounds.x) / m.bounds.w, (y - m.bounds.y) / m.bounds.h);
  // Convert derivatives from (u, v) to rest-space (x, y).
  s.dxu /= m.bounds.w;
  s.dyu /= m.bounds.w;
  s.dxv /= m.bounds.h;
  s.dyv /= m.bounds.h;
  return s;
}

export interface Tessellation {
  positions: Float32Array;
  texs: Float32Array;
  indices: number[];
  vertexCount: number;
}

/**
 * Triangulate the mesh for drawVertices. `texW/texH` is the image size; `padU/padV` adds an
 * outer ring of vertices whose texcoords fall outside the image (Decal tiling turns them
 * transparent) so the warped edge gets ~1px of linear-filter anti-aliasing.
 */
export function tessellate(m: MeshWarp, perPatch: number, texW: number, texH: number, padU = 0, padV = 0): Tessellation {
  const maxVerts = 65535;
  const ring = padU > 0 || padV > 0 ? 2 : 0;
  while (perPatch > 1 && (m.cols * perPatch + 1 + ring) * (m.rows * perPatch + 1 + ring) > maxVerts) perPatch--;
  const us: number[] = [];
  const vs: number[] = [];
  const nU = m.cols * perPatch, nV = m.rows * perPatch;
  if (ring) us.push(-padU);
  for (let k = 0; k <= nU; k++) us.push(k / nU);
  if (ring) us.push(1 + padU);
  if (ring) vs.push(-padV);
  for (let k = 0; k <= nV; k++) vs.push(k / nV);
  if (ring) vs.push(1 + padV);

  const W = us.length, H = vs.length;
  const positions = new Float32Array(W * H * 2);
  const texs = new Float32Array(W * H * 2);
  for (let j = 0; j < H; j++)
    for (let i = 0; i < W; i++) {
      const s = evalMesh(m, us[i], vs[j]);
      const k = (j * W + i) * 2;
      positions[k] = s.x;
      positions[k + 1] = s.y;
      texs[k] = us[i] * texW;
      texs[k + 1] = vs[j] * texH;
    }
  const indices: number[] = [];
  for (let j = 0; j < H - 1; j++)
    for (let i = 0; i < W - 1; i++) {
      const a = j * W + i, b = a + 1, c = a + W, d = c + 1;
      indices.push(a, b, d, a, d, c);
    }
  return { positions, texs, indices, vertexCount: W * H };
}

/** Pick a subdivision so each sub-quad is roughly `targetPx` screen pixels. */
export function autoSubdivision(m: MeshWarp, screenScale: number, targetPx = 6): number {
  let maxEdge = 0;
  const nu = latticeW(m), nv = latticeH(m);
  for (let j = 0; j < nv; j += 3)
    for (let i = 0; i + 3 < nu; i += 3) {
      // Control polygon length bounds the curve length.
      let l = 0;
      for (let a = 0; a < 3; a++) {
        const p = getPt(m, i + a, j), q = getPt(m, i + a + 1, j);
        l += Math.hypot(q.x - p.x, q.y - p.y);
      }
      maxEdge = Math.max(maxEdge, l);
    }
  for (let i = 0; i < nu; i += 3)
    for (let j = 0; j + 3 < nv; j += 3) {
      let l = 0;
      for (let b = 0; b < 3; b++) {
        const p = getPt(m, i, j + b), q = getPt(m, i, j + b + 1);
        l += Math.hypot(q.x - p.x, q.y - p.y);
      }
      maxEdge = Math.max(maxEdge, l);
    }
  return Math.max(2, Math.min(64, Math.ceil((maxEdge * screenScale) / targetPx)));
}

/** Rebuild the lattice for a new grid size, preserving the current warp as closely as possible. */
export function resizeMesh(m: MeshWarp, cols: number, rows: number): MeshWarp {
  const next = createMesh(m.bounds, cols, rows);
  next.autoInterior = m.autoInterior;
  const nu = latticeW(next), nv = latticeH(next);
  // Anchors are sampled from the old surface; handles are placed from its derivatives.
  for (let j = 0; j < nv; j += 3)
    for (let i = 0; i < nu; i += 3) {
      const u = i / (nu - 1), v = j / (nv - 1);
      const s = evalMesh(m, u, v);
      setPt(next, i, j, s);
      const du = 1 / (3 * cols), dv = 1 / (3 * rows);
      if (i + 1 < nu) setPt(next, i + 1, j, { x: s.x + s.dxu * du, y: s.y + s.dyu * du });
      if (i - 1 >= 0) setPt(next, i - 1, j, { x: s.x - s.dxu * du, y: s.y - s.dyu * du });
      if (j + 1 < nv) setPt(next, i, j + 1, { x: s.x + s.dxv * dv, y: s.y + s.dyv * dv });
      if (j - 1 >= 0) setPt(next, i, j - 1, { x: s.x - s.dxv * dv, y: s.y - s.dyv * dv });
    }
  recomputeInterior(next);
  return next;
}

export type PresetName = "none" | "arc" | "arch" | "bulge" | "flag" | "wave" | "fish" | "rise" | "fisheye" | "inflate" | "squeeze" | "twist";
export const PRESETS: PresetName[] = ["none", "arc", "arch", "bulge", "flag", "wave", "fish", "rise", "fisheye", "inflate", "squeeze", "twist"];

/**
 * Displacement field of an envelope preset at normalised (u, v), for a w × h box and a
 * bend of -1..1 (0 = no warp).
 */
export function presetDisplacement(name: PresetName, bend: number, w: number, h: number, u: number, v: number): [number, number] {
  const cu = 2 * u - 1, cv = 2 * v - 1;
  switch (name) {
    case "none":
      return [0, 0];
    case "arc":
      return [0, -bend * h * 0.35 * (1 - cu * cu)];
    case "arch":
      return [0, -bend * h * 0.35 * (1 - cu * cu) * (1 - v * 0.8)];
    case "bulge":
      return [0, -bend * h * 0.3 * (1 - cu * cu) * -cv];
    case "flag":
      return [0, bend * h * 0.15 * Math.sin(2 * Math.PI * u)];
    case "wave":
      return [0, bend * h * 0.15 * Math.sin(2 * Math.PI * u) * -cv];
    case "fish":
      return [0, bend * h * 0.25 * Math.sin(Math.PI * u) * -cv * (1 - Math.abs(cu) * 0.6)];
    case "rise":
      return [0, -bend * h * 0.25 * Math.sin(Math.PI * (u - 0.5))];
    case "fisheye":
    case "inflate": {
      const r2 = Math.min(1, cu * cu + cv * cv);
      const k = bend * (name === "fisheye" ? 0.35 : 0.5) * (1 - r2);
      return [cu * k * w * 0.5, cv * k * h * 0.5];
    }
    case "squeeze":
      return [-bend * w * 0.25 * cu * (1 - cv * cv), bend * h * 0.15 * cv * (1 - cu * cu)];
    case "twist": {
      const r = Math.min(1, Math.hypot(cu, cv));
      const a = bend * (Math.PI / 2) * (1 - r);
      const x = cu * Math.cos(a) - cv * Math.sin(a), y = cu * Math.sin(a) + cv * Math.cos(a);
      return [((x - cu) * w) / 2, ((y - cv) * h) / 2];
    }
  }
}

/**
 * Envelope presets (spec §5): anchors sit exactly on the displaced surface and each tangent
 * handle follows the field's derivative (Hermite → Bézier), so a coarse grid still traces the
 * envelope closely. The result is an ordinary mesh, so users can keep editing it by hand.
 */
export function applyPreset(m: MeshWarp, name: PresetName, bend: number) {
  m.pts = restLattice(m);
  const nu = latticeW(m), nv = latticeH(m);
  const { x: bx, y: by, w, h } = m.bounds;
  const P = (u: number, v: number): Pt => {
    const [dx, dy] = presetDisplacement(name, bend, w, h, u, v);
    return { x: bx + u * w + dx, y: by + v * h + dy };
  };
  const e = 1e-4;
  const du = 1 / (3 * m.cols), dv = 1 / (3 * m.rows);
  for (let j = 0; j < nv; j += 3)
    for (let i = 0; i < nu; i += 3) {
      const u = i / (nu - 1), v = j / (nv - 1);
      const a = P(u, v);
      setPt(m, i, j, a);
      // One-sided differences at the border keep the field inside its domain.
      const u0 = Math.max(0, u - e), u1 = Math.min(1, u + e), v0 = Math.max(0, v - e), v1 = Math.min(1, v + e);
      const pu0 = P(u0, v), pu1 = P(u1, v), pv0 = P(u, v0), pv1 = P(u, v1);
      const Du = { x: (pu1.x - pu0.x) / (u1 - u0), y: (pu1.y - pu0.y) / (u1 - u0) };
      const Dv = { x: (pv1.x - pv0.x) / (v1 - v0), y: (pv1.y - pv0.y) / (v1 - v0) };
      if (i + 1 < nu) setPt(m, i + 1, j, { x: a.x + Du.x * du, y: a.y + Du.y * du });
      if (i - 1 >= 0) setPt(m, i - 1, j, { x: a.x - Du.x * du, y: a.y - Du.y * du });
      if (j + 1 < nv) setPt(m, i, j + 1, { x: a.x + Dv.x * dv, y: a.y + Dv.y * dv });
      if (j - 1 >= 0) setPt(m, i, j - 1, { x: a.x - Dv.x * dv, y: a.y - Dv.y * dv });
    }
  recomputeInterior(m);
}

/** 12 boundary points + 4 corner texcoords of patch (pu, pv), in drawPatch order. */
export function patchBoundary(m: MeshWarp, pu: number, pv: number): number[] {
  const P = (a: number, b: number) => getPt(m, pu * 3 + a, pv * 3 + b);
  const order: [number, number][] = [
    [0, 0], [1, 0], [2, 0], [3, 0],
    [3, 1], [3, 2], [3, 3],
    [2, 3], [1, 3], [0, 3],
    [0, 2], [0, 1],
  ];
  return order.flatMap(([a, b]) => {
    const p = P(a, b);
    return [p.x, p.y];
  });
}
